import moment from 'moment';
import {Fragment, ReactNode, useState} from "react";
import {Tooltip} from "components/tooltip/Tooltip";
import timeIcon from "assets/16_time.svg";
import {Modal} from "components/modals/Modal";
import WorkflowDetailsUsers from "components/modals/WorkflowDetailsUsers.modal";
import okIcon from "assets/24_OKwhite.svg";
import activeIcon from "assets/24_hourglass.svg";
import lockedIcon from "assets/24_lock.svg";
import skippedIcon from "assets/24_replace.svg";
import rejectedIcon from "assets/24_rejected_document2.svg";
import canceledIcon from "assets/24_cancelled.svg";
import UserProfileTooltip from "components/tooltip/UserProfileTooltip.component";
import translate from "components/translations/translations.wrapper";
import {Assignment, Explanation, WorkflowDetailsWizardStepProps} from "types/workflowDetailsWizard";
import {WorkflowDetailsUsersMoreProps} from "types/modals";
import {StatusType, StepIconType} from "types/workflowDetailsWizardStep";
import {UserDTO} from "types/userDTO";
import {formatDate} from "utils/valueFormatter.function";
import {MODAL_ACTIONS, MODAL_SIZE} from "utils/constants";
import {useTooltipId} from "utils/tooltips";
import {useModalContext} from "components/modals/ModalContext";
import {CircleCheck, CircleX} from "lucide-react";
import {ICON_SIZE} from "components/common/constants";
import { SHARED_TOOLTIP_ID } from "components/tooltip/AppTooltip.component";

const tooltipContent = function (userDTO: UserDTO, explanations?: Array<Explanation>, actionLabel?: string, actionIcon?: ReactNode) {
    return (
        () => <Fragment>
            <UserProfileTooltip user={userDTO}>
                {actionLabel && <span className="d-inline-flex align-items-center">{actionLabel}{actionIcon}</span>}
            </UserProfileTooltip>
            {explanations &&
                explanations.map((explanation, index) => {
                    return <div key={`${explanation.action}-${index}`} className={"mt-2"}>
                        {explanation.action}
                    </div>
                })}
        </Fragment>
    )
}

function WorkflowDetailsWizardStep(props: WorkflowDetailsWizardStepProps) {
    const step = props.step || {status: StatusType.PASSED};
    const stepUid = useTooltipId();
    const stepIcons: StepIconType = {
        PASSED: okIcon,
        APPROVED: okIcon,
        CURRENT: activeIcon,
        ACTIVE: activeIcon,
        FUTURE: lockedIcon,
        SKIPPED: skippedIcon,
        REJECTED: rejectedIcon,
        CANCELED: canceledIcon
    };
    const statusClass = step.status.toLowerCase();
    // A user in completedAssignments has approved their line; anyone still incomplete is pending.
    const approvedUserIds = new Set((props.step?.completedAssignments ?? []).map((a) => a.userDTO.idUser));
    // Approved/rejected labels are only shown for Compello-sourced workflows.
    const isCompello = (props.data?.sourceApplication ?? "").toLowerCase().includes("compello");
    // ModalRouter is a sibling of the provider in PageLayout, so anything the router renders sits
    // outside it - inside SendForApproval this wizard has no host and mounts the same modal itself
    const {toggleModal} = useModalContext() || {};
    const [localMoreUsers, setLocalMoreUsers] = useState<WorkflowDetailsUsersMoreProps | null>(null);

    const closeLocalMoreUsers = function () {
        setLocalMoreUsers(null);
    }

    // Decision labels are hidden for FUTURE steps, matching the inline tooltip.
    const openMoreUsersModal = function (assignments: Array<Assignment>, status: StatusType) {
        const more = {
            assignments,
            completedAssignments: props.step.completedAssignments,
            isCompello: isCompello && status !== StatusType.FUTURE,
            title: `${assignments.length > 99 ? "99+" : assignments.length} ${props.translate("taskDetail.approvers")}`,
        };

        if (toggleModal) {
            toggleModal(MODAL_ACTIONS.WORKFLOW_DETAILS_USERS, more);
            return;
        }

        setLocalMoreUsers(more);
    }
    const computeActiveStepRenderClass = function (status: StatusType) {
        return status === StatusType.ACTIVE ? "wizard-step-details-highlight" : "wizard-step-details-text";
    }

    const tooltip = function (index: number, assignment: Assignment, status: StatusType) {
        const tooltipId = `${assignment.userDTO.idUser}-${status}-tooltip-${index}`;
        const approved = approvedUserIds.has(assignment.userDTO.idUser);
        const showDecisionLabel = isCompello && status !== StatusType.FUTURE;
        let actionLabel;
        if (showDecisionLabel) {
            actionLabel = approved ? props.translate("taskDetail.actionTypes.APPROVED") : props.translate("taskDetail.decisionPending");
        }
        const actionIcon = approved ? <CircleCheck size={ICON_SIZE.SMALL} className="ms-1"/> : undefined;
        return (
            <Fragment key={`${assignment.userDTO.idUser}-step`}>
                {index === 1 ? ', ' : ''}
                <span className="cursor-pointer"
                      data-for={SHARED_TOOLTIP_ID} data-tip={tooltipId}>
                                            {assignment.userDTO.firstName} {assignment.userDTO.lastName}
                                        </span>
                {actionLabel && <span className={"ms-2"}>({actionLabel})</span>}
                <Tooltip
                    id={tooltipId}
                    content={tooltipContent(assignment.userDTO, assignment.explanations, actionLabel, actionIcon)}
                />
            </Fragment>
        );
    }

    const extraApproversPopup = function (assignments: Array<Assignment>, status: StatusType) {
        return (
            <Fragment>
                {' '}{props.translate("taskDetail.and")}{' '}
                <span className="cursor-pointer"
                      onClick={() => openMoreUsersModal(assignments, status)}>
                                            {assignments.length > 99 ? '99+' : assignments.length - 2}{' '}
                    {props.translate("taskDetail.more")}
                                        </span>
            </Fragment>
        );
    }

    const renderErrors = function (errorExplanations: Array<string>) {
        return (
            <div className="wizard-step-details wizard-step-details-errors">
                {errorExplanations.map((error) => (
                    <div key={error}
                         className="wizard-step-details-error wizard-step-details-text text-error font-12 fst-italic">{error}</div>
                ))}
            </div>
        )
    }

    // @ts-ignore
    const renderStepDetailsTime = function (actionTime: Moment) {
        const formattedDate = formatDate(actionTime, null);
        const formattedTime = actionTime?.format('HH:mm');
        return (
            <div className="wizard-step-details-time">
                <img src={timeIcon} alt={"received time"}/>
                {formattedDate} at {formattedTime}
            </div>
        );
    }

    // @ts-ignore
    const renderDetailedStepDetails = function (status: StatusType, lastActionTime: moment.Moment) {
        const activeStatus = status === StatusType.ACTIVE || status === StatusType.CURRENT;
        const assignments = activeStatus ? props.step.completedAssignments.concat(props.step.incompleteAssignments) : props.step.completedAssignments;
        return (
            <Fragment>
                {assignments.length !== 0 &&
                    <div className={computeActiveStepRenderClass(status)}>
                        {activeStatus ? props.translate("taskDetail.pendingApprovalBy") : props.translate("taskDetail.approvedBy")}{' '}
                        { // @ts-ignore
                            assignments.slice(0, 2).map((assignment, index) => tooltip(index, assignment, status))
                        }
                        {assignments.length > 2 ? extraApproversPopup(assignments, status) : ''}
                    </div>}
                {!activeStatus && renderStepDetailsTime(lastActionTime)}
            </Fragment>
        )
    }

    // @ts-ignore
    const renderRejectedStepDetails = function (status: StatusType, lastActionTime: moment.Moment) {
        const rejectedBy = props.step.lastActionBy;
        const tooltipId = rejectedBy != null ? rejectedBy.idUser + "-" + status + "-tooltip" : "";
        const rejectedLabel = isCompello ? props.translate("taskDetail.actionTypes.REJECTED") : undefined;
        return (
            <Fragment>
                {rejectedBy == null ? <div
                        className="wizard-step-details-rejected">{props.translate("workflowProcessStatus.REJECTED")}</div> :
                    <div
                        className="wizard-step-details-rejected">{props.translate("taskDetail.rejectedBy")}{' '}{
                        <Fragment key={`${rejectedBy.idUser}-step`}>
                                <span className="cursor-pointer"
                                      data-for={SHARED_TOOLTIP_ID} data-tip={tooltipId}>
                            {rejectedBy.firstName} {rejectedBy.lastName}
                                </span>
                            {rejectedLabel && <span className={"ms-2"}>({rejectedLabel})</span>}
                            <Tooltip
                                id={tooltipId}
                                content={tooltipContent(rejectedBy, undefined, rejectedLabel, <CircleX size={ICON_SIZE.SMALL} className="ms-1"/>)}
                            />
                        </Fragment>
                    }</div>
                }
                {lastActionTime !== null && renderStepDetailsTime(lastActionTime)}
            </Fragment>
        )
    }

    // @ts-ignore
    const renderCanceledStepDetails = function (lastActionTime: moment.Moment) {
        const lastUpdateTime = props.data?.statusChanged ? (lastActionTime || moment(props.data.statusChanged)) : null;

        return (
            <Fragment>
                <div className="wizard-step-details-canceled">
                    {props.displaySourceApplication &&
                        <span>
                            {props.translate("workflowProcessStatus.CANCELLED")} {props.translate("taskDetail.from")} {props.displaySourceApplication}
                           </span>
                    }
                </div>
                {lastUpdateTime !== null && renderStepDetailsTime(lastUpdateTime)}
            </Fragment>
        )
    }

    const renderFutureStepDetails = function (status: StatusType) {
        const incompleteAssignments = props.step.incompleteAssignments
        return (
            incompleteAssignments.length !== 0 &&
            <div className="wizard-step-details-text wizard-step-details-future">
                {props.translate("taskDetail.tasksFor")}{' '}
                {  //@ts-ignore
                    incompleteAssignments.slice(0, 2).map((assignment, index) => tooltip(index, assignment, status))
                }
                {incompleteAssignments.length > 2 ? extraApproversPopup(incompleteAssignments, status) : ''}
            </div>
        )
    }

    const renderSkippedStepDetails = function () {
        return (
            <div className="wizard-step-details-skipped"
                 data-tip={`wizard-skipped-tooltip-${stepUid}`} data-for={SHARED_TOOLTIP_ID}>
                {props.step.explanation}
                <Tooltip id={`wizard-skipped-tooltip-${stepUid}`} content={() => props.step.explanation}/>
            </div>
        )
    }

    const renderStepDetails = function (status: StatusType) {
        if (props.approvalInitiated) {
            const approvalInitiatedUser = props.data.requester || props.data.submitter;
            const receivedTime = moment(props.data.received);
            const tooltipId = `${approvalInitiatedUser?.idUser}-${status}-approvalInitiated`;

            return (
                <Fragment>
                    {approvalInitiatedUser ?
                        <div className="wizard-step-details-text">
                        <span className="cursor-pointer"
                              data-for={SHARED_TOOLTIP_ID}
                              data-tip={tooltipId}>{approvalInitiatedUser.firstName} {approvalInitiatedUser.lastName} </span>
                            {props.translate("taskDetail.activatedFrom")} {props.data.displaySourceApplication}
                            <Tooltip
                                id={tooltipId}
                                content={tooltipContent(approvalInitiatedUser)}
                            />
                        </div> :
                        <div className="wizard-step-details-text">
                            {props.translate("taskDetail.activatedFromNoUser")} {props.data.displaySourceApplication}
                        </div>
                    }
                    {renderStepDetailsTime(receivedTime)}
                </Fragment>
            )
        }
        const lastActionTime = props.step.lastAction ? moment(props.step.lastAction) : null;
        switch (status) {
            case StatusType.ACTIVE:
            case StatusType.CURRENT:
            case StatusType.PASSED:
            case StatusType.APPROVED:
                return renderDetailedStepDetails(status, lastActionTime);
            case 'REJECTED':
                return renderRejectedStepDetails(status, lastActionTime);
            case 'CANCELED':
                return renderCanceledStepDetails(lastActionTime);
            case 'FUTURE':
                return renderFutureStepDetails(status);
            case 'SKIPPED':
                return renderSkippedStepDetails();
            default:
                return null
        }
    }

    return (
        <div className="wizard-step-wrapper">
            <div className={`wizard-step ${statusClass}`}
                 data-tip={`wizard-step-tooltip-${stepUid}`} data-for={SHARED_TOOLTIP_ID}>
                <div className="wizard-step-content">
                    <div className="wizard-step-icon">
                        <img src={stepIcons[step.status]} alt={"step icon"}/>
                    </div>
                    <div className="wizard-step-title">
                        {props.children}
                    </div>
                </div>
                <Tooltip id={`wizard-step-tooltip-${stepUid}`} content={() => props.children || ''}/>
            </div>
            {props.step?.errorExplanations?.length > 0 ? renderErrors(props.step.errorExplanations) : ''}

            <div className="wizard-step-details">
                {renderStepDetails(step.status)}
            </div>

            {localMoreUsers &&
                <Modal
                    open={true}
                    onClose={closeLocalMoreUsers}
                    title={localMoreUsers.title}
                    size={MODAL_SIZE.SMALL}
                >
                    {({loopFocus, initialFocusRef}) => (
                        <WorkflowDetailsUsers
                            more={localMoreUsers}
                            closeModal={closeLocalMoreUsers}
                            loopFocus={loopFocus}
                            initialFocusRef={initialFocusRef}
                            // the list only renders what it was handed, so it never raises one
                            errorCallback={() => undefined}
                        />
                    )}
                </Modal>
            }
        </div>
    );
}

const withTranslations = translate(WorkflowDetailsWizardStep);
export default withTranslations;
